import { useEffect, useState } from 'react'
import { Sparkles, MessageCircle, Wallet } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import SearchableSelect from '../components/SearchableSelect'
import { successModal, errorModal } from '../components/ui'

const PROVIDERS = [
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'deepseek', label: 'DeepSeek' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
]

export default function IAConfig() {
  const [cfg, setCfg] = useState(null)
  const [saldo, setSaldo] = useState(null)
  const [modelos, setModelos] = useState([])

  function carregar() {
    api.get('/ia/config').then((r) => setCfg(r.data || {})).catch(() => setCfg({}))
    api.get('/ia/saldo-openrouter').then((r) => setSaldo(r.data)).catch(() => {})
  }
  useEffect(carregar, [])

  // Modelos do provedor (OpenRouter = catálogo real; recarrega ao trocar provedor)
  useEffect(() => {
    if (!cfg?.provider) return
    api.get(`/ia/modelos?provider=${encodeURIComponent(cfg.provider)}`).then((r) => setModelos(r.data?.data || [])).catch(() => {})
  }, [cfg?.provider])

  async function salvar() {
    try {
      const body = {
        provider: cfg.provider, modelo: cfg.modelo, api_key: cfg.api_key, api_url: cfg.api_url,
        max_tokens: cfg.max_tokens ? Number(cfg.max_tokens) : null, temperatura: cfg.temperatura, observacoes: cfg.observacoes,
      }
      await api.put('/ia/config', body); successModal('Configuração da IA salva!'); carregar()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao salvar') }
  }

  if (!cfg) return <div className="flex justify-center py-20"><Loader text="Carregando IA..." /></div>
  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'
  const set = (k, v) => setCfg({ ...cfg, [k]: v })
  const modeloOpts = (() => {
    const arr = [...modelos]
    if (cfg.modelo && !arr.some((m) => String(m.value) === String(cfg.modelo))) arr.unshift({ value: cfg.modelo, label: `${cfg.modelo} (atual)` })
    return arr
  })()

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900 flex items-center gap-2"><Sparkles size={22} className="text-accent-600" /> IA TheConect 360°</h1>
          <p className="text-gray-500 text-sm mt-0.5">Opções e configuração do assistente inteligente.</p>
        </div>
        <button onClick={() => window.dispatchEvent(new Event('tc-ia-open'))}
          className="flex items-center gap-1.5 rounded-xl border border-accent-200 text-accent-700 px-4 py-2 text-sm font-semibold hover:bg-accent-50">
          <MessageCircle size={16} /> Abrir assistente (chat)
        </button>
      </div>

      {saldo && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-400 flex items-center justify-center text-white"><Wallet size={20} /></div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Saldo OpenRouter</p>
            <p className="font-heading text-xl font-bold text-gray-900">US$ {Number(saldo.saldo || 0).toFixed(2)}</p>
            <p className="text-[11px] text-gray-400">créditos {Number(saldo.total_credits || 0).toFixed(2)} · uso {Number(saldo.total_usage || 0).toFixed(2)}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Configuração do provedor</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Provedor</label>
            <SearchableSelect value={cfg.provider || ''} onChange={(v) => set('provider', v)} options={PROVIDERS} emptyOption={false} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Modelo</label>
            <SearchableSelect value={cfg.modelo || ''} onChange={(v) => set('modelo', v)} emptyOption={false}
              placeholder="Buscar modelo..." options={modeloOpts} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">API Key / Token</label>
            <input type="password" value={cfg.api_key || ''} onChange={(e) => set('api_key', e.target.value)} className={inputCls} placeholder="cole a chave do provedor" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">API URL (opcional)</label>
            <input value={cfg.api_url || ''} onChange={(e) => set('api_url', e.target.value)} className={inputCls} placeholder="https://openrouter.ai/api/v1/chat/completions" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Máx. tokens</label>
            <input type="number" value={cfg.max_tokens || ''} onChange={(e) => set('max_tokens', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Temperatura</label>
            <input value={cfg.temperatura || ''} onChange={(e) => set('temperatura', e.target.value)} className={inputCls} placeholder="0.7" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Observações / contexto base</label>
            <textarea value={cfg.observacoes || ''} onChange={(e) => set('observacoes', e.target.value)} rows={3} className={inputCls} />
          </div>
        </div>
        <button onClick={salvar} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-6 py-2.5 font-semibold shadow-md hover:brightness-95">Salvar configuração</button>
      </div>
    </div>
  )
}
